import {
	collabRegistryId,
	documentStoreV2Id,
	metadataStoreId,
	pageAuthorityId,
	presenceStoreId,
	requestPageStoreId,
} from "@moment/api-collab/id";
import {
	DocumentCollabRegistry,
	PageAuthority,
	PresenceStore,
	Store as RebootStore,
	StoreV2,
} from "@moment/reboot-differential";

/**
 * Reference props for document-level resources (e.g., metadata).
 */
export interface DocumentRefProps {
	workspaceSlug: string;
	documentId: string;
	branch: string;
	sha: string;
}

/**
 * Reference props for page-level resources (e.g., editor state).
 * Extends DocumentRefProps with pageId.
 */
export interface PageRefProps extends DocumentRefProps {
	pageId: string;
}

/**
 * Create a PageAuthority reference for a specific page in a collab session
 */
export const mdPageAuthorityRef = (props: PageRefProps): PageAuthority.WeakReference => {
	const { workspaceSlug, documentId, branch, pageId, sha } = props;
	const authorityId = pageAuthorityId(workspaceSlug, documentId, branch, pageId, sha);
	return PageAuthority.ref(authorityId);
};

/**
 * Create a PresenceStore reference for a specific page in a collab session
 */
export const mdPresenceStoreRef = (props: PageRefProps): PresenceStore.WeakReference => {
	const { workspaceSlug, documentId, branch, pageId, sha } = props;
	const storeId = presenceStoreId(workspaceSlug, documentId, branch, pageId, sha);
	return PresenceStore.ref(storeId);
};

/**
 * Create a RebootStore reference for document metadata in a collab session
 */
export const metadataRef = (props: DocumentRefProps): RebootStore.WeakReference => {
	const { workspaceSlug, documentId, branch, sha } = props;
	const storeId = metadataStoreId(workspaceSlug, documentId, branch, sha);
	return RebootStore.ref(storeId);
};

/**
 * Create a DocumentCollabRegistry reference for a workspace
 */
export const collabRegistryRef = (workspaceSlug: string): DocumentCollabRegistry.WeakReference => {
	const registryId = collabRegistryId(workspaceSlug);
	return DocumentCollabRegistry.ref(registryId);
};

/**
 * Create a RebootStore reference for an HTTP request page in a collab session
 */
export const requestPageRef = (props: PageRefProps): RebootStore.WeakReference => {
	const { workspaceSlug, documentId, branch, pageId, sha } = props;
	const storeId = requestPageStoreId(workspaceSlug, documentId, branch, pageId, sha);
	return RebootStore.ref(storeId);
};

/**
 * Create a StoreV2 reference for a document's v2 store in a collab session
 */
export const documentStoreV2Ref = (props: DocumentRefProps): StoreV2.WeakReference => {
	const { workspaceSlug, documentId, branch, sha } = props;
	const storeId = documentStoreV2Id(workspaceSlug, documentId, branch, sha);
	return StoreV2.ref(storeId);
};
